import React from 'react';
import { dersLabel, maarifKazanimMi, kartBasligi } from '../lib/dersler';
import { BAGLAM_ROZET_BASLIK } from '../lib/baglamRozet';
import SoruKarti, { LatexParagraphs, TikzDiagram, GorselDiagram } from './SoruKarti';

/**
 * Yönerge cümlesindeki numara ARALIĞI — "1 - 2." / "1 - 3."
 *
 * Kullanıcı kararı 2026-08-17 (#28922/#28923): yönerge cümlesi `Ortak Bilgi`
 * başlığının HEMEN ALTINDA, bağlam metninden ÖNCE durur. Numaralar grup içi
 * sıradır (ÖSYM kitapçık üslubu); basım tarafı ayrıca kitapçık numaralarını
 * kullanır (`pdfExport.js _grupYonergeCumlesi`).
 *
 * ⚠️ 2026-09-21: biçim LİSTE'den ARALIK'a geçti ("1. ve 2." -> "1 - 2."). Ölçüm
 * (PyMuPDF): 2026-TYT kitapçığı s.14/15/16 + Mebi_TYT_18_25 korpusu = 7/7
 * örnekte ÖSYM aralık kullanıyor, tire BOŞLUKLU. Eski liste biçimi ("1., 2. ve
 * 3.") ÖSYM'de hiç geçmiyor. Panel ve basım aynı kalıbı basar.
 */
export function yonergeNumaralari(adet) {
  const n = Number(adet) || 0;
  if (n <= 0) return '';
  return n === 1 ? '1.' : `1 - ${n}.`;
}

/**
 * İKİ SÜTUNLU VİTRİN YERLEŞİMİ (öğretmen/yayınevi geri bildirimi 2026-08-18)
 *
 * Geniş ekranda solda ortak kök (yapışkan — üyeler kaydırılırken bağlam ekranda
 * kalır), sağda üyeler. ÖSYM kitapçığının iki sütunlu okuma düzenini taklit eder.
 *
 * Yalnız GERÇEK grupta açılır. Tek üyeli ya da kökü olmayan grupta ikinci sütun
 * boş kalır ve düzen bugünkünden KÖTÜ görünür — o hâlde tek sütuna düşülür.
 * Dar ekranda (lg altı) her iki durumda da tek sütun; sınıflar `lg:` önekli.
 */
export function ikiSutunMu(grup) {
  const uyeSayisi = (grup && Array.isArray(grup.sorular)) ? grup.sorular.length : 0;
  const kok = String((grup && grup.ortak_kok) || '').trim();
  return uyeSayisi >= 2 && kok.length > 0;
}

/**
 * BAĞLAM TEMELLİ SORU GRUBU — birleşik kart (KURALLAR §19.3)
 *
 * Öğretmen geri bildirimi 2026-08-12 (#19851 → #27415 + #27416):
 *   "Bu soru alt-soru olduğundan 3 soru gibi değil, 2 soru cevapları ayrı ayrı
 *   bağlam temelli soru olabilir." + "her bir soruda bağlam temelli soru altında
 *   kaç adet soru olduğu belirtilecek."
 *
 * DB'de İKİ AYRI KAYIT durur (bir kayıt = bir soru = bir cevap, §19.1). Bu bileşen
 * onları TEK ÇATI altında gösterir:
 *   - ortak kök + görsel BİR KEZ (üstte),
 *   - altında her üye kendi `SoruKarti`'sıyla: kendi 5 şıkkı, kendi doğru cevabı,
 *     kendi çözümü, kendi puan/onay/red butonları (§19.3 md.6 — onay kayıt başına).
 *
 * Kök, üyelerin `soru_metni_latex`'inde TEKRAR eder (saklama sözleşmesi); üyede
 * `SoruKarti` onu `kokKirp` ile kırpar. Kırpma eşleşmezse metin olduğu gibi kalır
 * (kök iki kez görünür — bozuk render'dan iyidir).
 */
export default function BaglamGrupKarti({
  grup,                 // { baglam_grubu, ortak_kok, baglam_adi, toplam, eksik_uye, sorular }
  onOnayla, onPuanKaydet, onPratikOnayla, onVideoOnayla, onKorCevap, onKorPuan, onCevapQcKanit, onCevapQcIstek, onGeri,
}) {
  const uyeler = (grup && Array.isArray(grup.sorular)) ? grup.sorular : [];
  if (uyeler.length === 0) return null;

  const ilk = uyeler[0];
  const toplam = grup.toplam || uyeler.length;
  const ortakKok = grup.ortak_kok || '';
  const gorselDeger = ilk.gorsel_base64 || ilk.gorsel_url;
  const ikiSutun = ikiSutunMu(grup);

  return (
    <div className={`mx-auto font-serif ${ikiSutun ? 'max-w-3xl lg:max-w-6xl' : 'max-w-3xl'}`}>
      <button onClick={onGeri} className="px-4 py-2 rounded-lg border border-gray-400 bg-white cursor-pointer mb-4 text-sm hover:bg-gray-50">← Geri Dön</button>

      {/* GRUP BAŞLIĞI — rozet + kapsanan soru sayısı */}
      <div className="mb-4">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <span className={BAGLAM_ROZET_BASLIK}
                title="Ortak kök altında birden çok bağımsız soru — KURALLAR §19">
            🔗 BAĞLAM TEMELLİ SORU · {toplam} SORU
          </span>
          {uyeler.map(u => (
            <span key={u.id} className="inline-block text-white px-2 py-0.5 rounded-md text-sm font-bold"
                  style={{ backgroundColor: '#111827' }}>#{u.public_no}</span>
          ))}
        </div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">
          {grup.baglam_adi || kartBasligi(ilk)}
        </h2>
        <div className="flex gap-2 flex-wrap">
          <span className="text-white px-2.5 py-1 rounded text-xs font-semibold" style={{ backgroundColor: '#1976D2' }}>{dersLabel(ilk.ders)}</span>
          {maarifKazanimMi(ilk.kazanim_kodu, ilk.mufredat_efektif) && (
            <span className="text-white px-2.5 py-1 rounded text-xs font-bold" style={{ backgroundColor: '#059669' }} title="Maarif 2024 müfredatı — 2028+ YKS hedefli">MAARİF</span>
          )}
          {ilk.kazanim_kodu && (
            <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold">{ilk.kazanim_kodu}</span>
          )}
          {ilk.alt_konu && (
            <span className="bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-xs font-semibold">{ilk.alt_konu}</span>
          )}
        </div>
      </div>

      {/* PARÇALI GRUP UYARISI — grup_toplam ile gelen üye sayısı tutmuyorsa */}
      {grup.eksik_uye && (
        <div className="bg-orange-50 border-2 border-orange-400 rounded-lg px-4 py-2.5 mb-3 text-sm text-orange-800">
          <strong>Eksik grup</strong> — bu bağlam {toplam} soru içermeli ama {uyeler.length} kayıt bulundu.
          Eksik soru reddedilmiş veya hiç içe aktarılmamış olabilir.
        </div>
      )}

      {/* İKİ SÜTUN: solda ortak kök (yapışkan), sağda üyeler. Dar ekranda alt alta. */}
      <div className={ikiSutun
        ? 'lg:grid lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] lg:gap-6 lg:items-start'
        : ''}>
        <div className={ikiSutun ? 'lg:sticky lg:top-4' : ''}>
          {/* ORTAK KÖK + GÖRSEL — grup başına BİR KEZ */}
          <div className="bg-white rounded-xl p-5 mb-4 shadow-sm border-l-4 border-indigo-400">
            <h3 className="text-base font-semibold text-indigo-700 mt-0 mb-2">
              Ortak Bilgi
            </h3>
            {/* YÖNERGE — başlığın HEMEN ALTINDA, bağlam metninden ÖNCE (§19.3 md.1).
                2026-09-21: italik gri satır -> ÖSYM'deki gibi KALIN metin + siyah
                çerçeveli kutu (basımla aynı cümle ve aynı görünüm). */}
            <div className="mb-3 px-3 py-2 border-2 border-gray-900 text-sm text-gray-900 font-bold">
              {yonergeNumaralari(uyeler.length)} soruları aşağıdaki parçaya göre cevaplayınız.
            </div>
            {ortakKok
              ? <LatexParagraphs className="text-base text-gray-800 mb-2">{ortakKok}</LatexParagraphs>
              : <div className="text-sm text-gray-400 mb-2 italic">(ortak kök metni kayıtlı değil — sorular kendi gövdelerini gösterir)</div>
            }
            {ilk.diyagram_tikz && <TikzDiagram tikzCode={ilk.diyagram_tikz} />}
            {gorselDeger && <GorselDiagram gorsel={gorselDeger} />}
          </div>
        </div>

        {/* ÜYELER — her biri kendi şıkkı, cevabı, puanı ve onayıyla */}
        <div className="min-w-0">
          {uyeler.map((u, i) => (
            <div key={u.id} className="mb-6 pl-3 border-l-2 border-indigo-100">
              <SoruKarti
                soru={u}
                grup={{
                  sira: Number(u.kaynak_detay && u.kaynak_detay.grup_sira) || (i + 1),
                  toplam,
                  ortakKok,
                }}
                onOnayla={onOnayla}
                onPuanKaydet={onPuanKaydet}
                onPratikOnayla={onPratikOnayla}
                onVideoOnayla={onVideoOnayla}
                onKorCevap={onKorCevap}
                onKorPuan={onKorPuan}
                onCevapQcKanit={onCevapQcKanit}
                onCevapQcIstek={onCevapQcIstek}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
